export class ApiClient {
	constructor(baseUrl) {
		this.baseUrl = baseUrl || (import.meta.env?.VITE_API_BASE_URL) || '';
		this.token = null;
		this.refreshCallback = null; // Will be set by AuthContext
		this.isRefreshing = false;
		this.failedQueue = [];
	}

	setToken(token) {
		this.token = token;
	}

	setRefreshCallback(callback) {
		this.refreshCallback = callback;
	}

	processQueue(error, token = null) {
		this.failedQueue.forEach(prom => {
			if (error) {
				prom.reject(error);
			} else {
				prom.resolve(token);
			}
		});
		this.failedQueue = [];
	}

	async request(path, { method = 'GET', body, params, headers = {}, skipAuthRefresh = false } = {}) {
		const url = new URL(path, this.baseUrl || window.location.origin);
		if (params && typeof params === 'object') {
			Object.keys(params).forEach(key => {
				if (params[key] != null) url.searchParams.append(key, params[key]);
			});
		}

		// Check if the body is FormData
		const isFormData = body instanceof FormData;

		const opts = {
			method,
			headers: {
				...(!isFormData && { 'Content-Type': 'application/json' }),
				...headers
			}
		};

		if (this.token) opts.headers.Authorization = `Bearer ${this.token}`;
		if (body !== undefined && body !== null && method !== 'GET' && method !== 'HEAD') {
        opts.body = isFormData ? body : JSON.stringify(body);}

		try {
			const res = await fetch(url.toString(), opts);
			const contentType = res.headers.get('content-type') || '';
			let data = null;
			
			if (contentType.includes('application/json')) {
				data = await res.json();
			} else {
				data = await res.text();
			}

			if (!res.ok) {
				// Handle 401 Unauthorized - token might be expired
				if (res.status === 401 && this.refreshCallback && !skipAuthRefresh) {
					// Don't try to refresh on login/register/refresh-token endpoints
					if (path.includes('/auth/login') || 
					    path.includes('/auth/register') || 
					    path.includes('/auth/refresh-token')) {
						const err = new Error(`API request failed: ${res.status} ${res.statusText}`);
						err.status = res.status;
						err.data = data;
						throw err;
					}

					// If already refreshing, queue this request
					if (this.isRefreshing) {
						return new Promise((resolve, reject) => {
							this.failedQueue.push({ resolve, reject });
						})
						.then(() => {
							// Retry the original request with new token
							return this.request(path, { method, body, params, headers, skipAuthRefresh });
						})
						.catch(err => {
							throw err;
						});
					}

					// Start refresh process
					this.isRefreshing = true;

					try {
						// Attempt to refresh the token
						const refreshSuccess = await this.refreshCallback();
						
						if (refreshSuccess) {
							// Token refreshed successfully, process queued requests
							this.processQueue(null, this.token);
							this.isRefreshing = false;
							
							// Retry the original request
							return this.request(path, { method, body, params, headers, skipAuthRefresh });
						} else {
							// Refresh failed
							const err = new Error('Token refresh failed');
							err.status = 401;
							this.processQueue(err, null);
							this.isRefreshing = false;
							throw err;
						}
					} catch (refreshError) {
						this.processQueue(refreshError, null);
						this.isRefreshing = false;
						throw refreshError;
					}
				}

				// For other errors, just throw
				const err = new Error(`API request failed: ${res.status} ${res.statusText}`);
				err.status = res.status;
				err.data = data;
				throw err;
			}

			return data;
		} catch (error) {
			// Re-throw the error
			throw error;
		}
	}

	get(path, params) {
		return this.request(path, { method: 'GET', params });
	}

	post(path, body) {
		return this.request(path, { method: 'POST', body });
	}

	put(path, body) {
		return this.request(path, { method: 'PUT', body });
	}

	patch(path, body) {
		return this.request(path, { method: 'PATCH', body });
	}

	delete(path) {
		return this.request(path, { method: 'DELETE' });
	}
}

export default new ApiClient((import.meta.env?.VITE_API_BASE_URL) || '/');
